import { AgGridReact } from '@mint-lib/ag-grid/react';
import { useCallback, useMemo, useState, type RefObject } from 'react';

import {
  ClientSideRowModelApiModule,
  ColumnAutoSizeModule,
  DateFilterModule,
  ModuleRegistry,
  PaginationModule,
  RenderApiModule,
  RowSelectionModule,
  SelectionChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
  type ICellRendererParams,
} from '@mint-lib/ag-grid/community';
import {
  CellSelectionModule,
  ClientSideRowModelModule,
  ColumnMenuModule,
  ContextMenuModule,
  LicenseManager,
  RowGroupingModule,
  RowSelectionOptions,
  SetFilterModule,
} from '@mint-lib/ag-grid/enterprise';

import { getDocumentStatus } from '../../../helpers/ragDocumentStatus.js';
import { RagTableType } from '../../../types/rag.js';
import {
  ActionsCell,
  DescriptionCell,
  FileTypeCell,
  StatusCell,
  UploadedDateCell,
  UserCell,
} from './cells/index.js';
import { dateFilterParams } from './dateFilterParams.js';
import { KnowledgeBaseNoRowOverlay } from './NoRowsOverlay.jsx';

ModuleRegistry.registerModules([
  RowGroupingModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  RenderApiModule,
  PaginationModule,
  ColumnAutoSizeModule,
  RowGroupingModule,
  RowSelectionModule,
  ContextMenuModule,
  ColumnMenuModule,
  SetFilterModule,
  DateFilterModule,
  CellSelectionModule,
]);

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

type Props = {
  tableData: RagTableType[];
  onSelectTableRows: (rows: RagTableType[]) => void;
  onUnselectTableRows: () => void;
  onOpenDeleteDialog: (rows: RagTableType[]) => void;
  onOpenEditPermissionsDialog: (document: RagTableType) => void;
  gridRef: RefObject<AgGridReact | null>;
};

const RagTable = ({
  tableData,
  onSelectTableRows,
  onUnselectTableRows,
  onOpenDeleteDialog,
  onOpenEditPermissionsDialog,
  gridRef,
}: Props) => {
  const [columnDefs] = useState([
    {
      headerName: 'Name',
      valueGetter: (p: ValueGetterParams) => {
        return p.data?.fileName;
      },
      filter: true,
    },
    {
      headerName: 'Type',
      field: 'fileType',
      cellRenderer: FileTypeCell,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
      },
      filter: true,
    },
    {
      headerName: 'Description',
      field: 'description',
      cellRenderer: DescriptionCell,
    },
    {
      headerName: 'Uploaded date',
      field: 'uploadedDate',
      cellRenderer: UploadedDateCell,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
    },
    {
      headerName: 'Uploaded by',
      field: 'uploadedBy',
      cellRenderer: UserCell,
      valueGetter: (p: ValueGetterParams) =>
        `${p.data.firstName}_${p.data.lastName}`,
      filter: true,
      filterParams: {
        valueFormatter: (params: ValueFormatterParams) => {
          const [firstName, lastName] = params.value.split('_');
          return `${firstName} ${lastName}`;
        },
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      cellRenderer: StatusCell,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
      },
      filter: true,
      filterParams: {
        valueFormatter: (params: ValueFormatterParams) => {
          const statusValue = getDocumentStatus(params.value).label;

          return statusValue.charAt(0).toUpperCase() + statusValue.slice(1);
        },
      },
    },
    {
      headerComponent: () => null,
      field: 'actions',
      pinned: 'right' as boolean | 'left' | 'right' | null | undefined,
      filter: false,
      sortable: false,
      resizable: false,
      width: 40,
      cellRenderer: (data: ICellRendererParams<RagTableType>) => (
        <ActionsCell
          data={data.data}
          onOpenDeleteDialog={onOpenDeleteDialog}
          onUnselectTableRows={onUnselectTableRows}
          onOpenEditPermissionsDialog={onOpenEditPermissionsDialog}
          className="w-full"
        />
      ),
      cellClass: '!p-0',
    },
  ]);

  const rowSelection: RowSelectionOptions<RagTableType> = useMemo(() => {
    return {
      mode: 'multiRow' as 'multiRow' | 'singleRow',
      hideDisabledCheckboxes: true,
      //FYI: Row selection is used to delete files. We must reject row selection for data that has not yet been processed and put into context
      isRowSelectable: (rowNode) => rowNode?.data?.status !== 'pending',
    };
  }, []);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);

      onSelectTableRows(selectedData);
    },
    [onSelectTableRows],
  );

  return (
    <div className="h-full w-full" data-testid="rag-table">
      <AgGridReact
        ref={gridRef}
        rowSelection={rowSelection}
        rowData={tableData}
        columnDefs={columnDefs}
        rowModelType={'clientSide'}
        cacheBlockSize={20}
        loadThemeGoogleFonts
        paginationAutoPageSize
        pagination
        autoSizeStrategy={{ type: 'fitGridWidth' }}
        onSelectionChanged={onSelectionChanged}
        suppressMovableColumns
        suppressContextMenu
        components={{
          agNoRowsOverlay: KnowledgeBaseNoRowOverlay,
        }}
      />
    </div>
  );
};

export { RagTable };
